import React, { useContext, useState, useEffect, useCallback } from 'react'
import { Row, Col, Button, FormControl } from 'react-bootstrap'
import { VendorContext } from '../../../contexts/vendorContext'
import GiftsBundleItem from './bundleItem'
import UnbundledItemCard from './unbundledItemCard'
import GiftBundleModal from './giftBundleModal'
import { Link } from 'react-router-dom'
import {
  getBundledGifts,
  getUnbundledGifts
} from '../../../services/giftService'
import { GiftBundleCard } from '../../../models/GiftBundle'
import { Gift } from '../../../models/Gift'
import { ButtonName } from '../../../config/enums'

enum FilterField {
  gift = 'Gift',
  giftBundle = 'Gift Bundle'
}

const GiftsManagement = () => {
  const vendorContext = useContext(VendorContext)
  const vendorId = vendorContext?.profile?.vendorId
  const [showCreateGiftBundleModal, setShowCreateGiftBundleModal] = useState(
    false
  )
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState(FilterField.gift)
  const [giftBundlesList, setGiftBundlesList] = useState<GiftBundleCard[]>([])
  const [isUnbundledVisible, setIsUnbundledVisible] = useState(true)
  const [isBundledVisible, setIsBundledVisible] = useState(true)
  const [unbundledGiftsList, setUnbundledGiftsList] = useState<Gift[]>([])

  const getBundled = useCallback(async () => {
    setGiftBundlesList(await getBundledGifts(vendorId!))
  }, [vendorId])

  const getUnbundled = useCallback(async () => {
    setUnbundledGiftsList(await getUnbundledGifts(vendorId!))
  }, [vendorId])

  const searchUnbundled = () => {
    setIsUnbundledVisible(true)
    setIsBundledVisible(false)
    if (search === undefined) {
      getUnbundled()
    } else {
      getUnbundledGifts(vendorId!).then(results => {
        const searchUnbundledArray = results.filter((unbundledGift: Gift) => {
          let name = unbundledGift.name.split('')
          let match = []
          for (var i = 0; i < search.length; i++) {
            match.push(name[i])
          }
          return search.toLowerCase() === match.join('').toLowerCase()
        })
        setUnbundledGiftsList(searchUnbundledArray)
      })
    }
  }

  const searchBundled = () => {
    setIsBundledVisible(true)
    setIsUnbundledVisible(false)

    if (search === undefined) {
      getBundled()
    } else {
      getBundledGifts(vendorId!).then(results => {
        const searchBundledArray = results.filter(
          (giftBundle: GiftBundleCard) => {
            let name = giftBundle.name.split('')
            let match = []
            for (var i = 0; i < search.length; i++) {
              match.push(name[i])
            }
            return search.toLowerCase() === match.join('').toLowerCase()
          }
        )

        setGiftBundlesList(searchBundledArray)
      })
    }
  }

  useEffect(() => {
    getUnbundled()
    getBundled()
  }, [vendorContext, getBundled, getUnbundled])

  useEffect(() => {
    if (search === undefined || search === '') {
      setIsBundledVisible(true)
      setIsUnbundledVisible(true)
      getUnbundled()
      getBundled()
    }
  }, [search, isUnbundledVisible, isBundledVisible, getBundled, getUnbundled])

  return (
    <>
      <GiftBundleModal
        show={showCreateGiftBundleModal}
        type={ButtonName.add}
        handleClose={() => {
          setShowCreateGiftBundleModal(false)
        }}
      />

      <Row data-testid="gifts-management">
        <Col className="pt-5 pb-5">
          <h1 className="largeTitle">Gifts Management</h1>
        </Col>
      </Row>

      <Row className="pb-5">
        <Col className="pr-0 pt-1 " xs={2}>
          <FormControl
            data-testid="search-input"
            size="sm"
            type="text"
            className=" pr-0"
            onChange={async (e: React.FormEvent<HTMLInputElement>) => {
              setSearch(e.currentTarget.value)
            }}
            placeholder="Enter search term"
          />
        </Col>

        <Col xs={1} className="pt-1">
          <Button
            variant="primary"
            size="sm"
            data-testid="search-button"
            onClick={async () => {
              if (filter === FilterField.gift) {
                searchUnbundled()
              } else {
                searchBundled()
              }
            }}
          >
            Search
          </Button>
        </Col>

        <Col xs={2} className="pt-1">
          <FormControl
            as="select"
            size="sm"
            data-testid="search-filter"
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              e.currentTarget.value === FilterField.gift
                ? setFilter(FilterField.gift)
                : setFilter(FilterField.giftBundle)
            }}
            value={filter}
          >
            <option value={FilterField.gift}>{FilterField.gift}</option>
            <option value={FilterField.giftBundle}>
              {FilterField.giftBundle}
            </option>
          </FormControl>
        </Col>

        <Col xs={{ span: 4, offset: 3 }} className="text-right">
          <Button
            variant="secondary"
            data-testid="create-bundle-button"
            className="mr-3"
            onClick={() => {
              setShowCreateGiftBundleModal(true)
            }}
          >
            Create bundle
          </Button>

          <Link to="/vendor/creategift">
            <Button variant="secondary" data-testid="create-gift-button">
              Create gift
            </Button>
          </Link>
        </Col>
      </Row>

      {isUnbundledVisible && (
        <>
          <Row>
            <Col>
              <h3>Unbundled</h3>
            </Col>
          </Row>

          <Row className="pb-5">
            {unbundledGiftsList.length >= 1 ? (
              unbundledGiftsList.map((gift: Gift) => {
                return (
                  <UnbundledItemCard
                    key={gift.id}
                    imageSrc={gift.imageUrl}
                    itemActive={gift.isActive}
                    itemTitle={gift.name}
                    itemButtonUrl={`/vendor/editgift/${gift.id}`}
                  />
                )
              })
            ) : (
              <Col>No results.</Col>
            )}
          </Row>
        </>
      )}

      {isBundledVisible ? (
        giftBundlesList.length >= 1 ? (
          giftBundlesList.map((bundle: GiftBundleCard, index) => {
            return (
              <GiftsBundleItem
                key={bundle.id}
                gifts={bundle.gifts}
                name={bundle.name}
                giftBundleId={bundle.id}
              />
            )
          })
        ) : (
          <Row>
            <Col>No results.</Col>
          </Row>
        )
      ) : null}
    </>
  )
}

export default GiftsManagement
